import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { Project } from '../../types/project';

export function useProjects() {
  return useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/project'),
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
      api.put<Project>(`/project/${id}/${favorite ? 'favorite' : 'unfavorite'}`),
    onMutate: async ({ id, favorite }) => {
      await queryClient.cancelQueries({ queryKey: ['projects'] });
      const prev = queryClient.getQueryData<Project[]>(['projects']);
      queryClient.setQueryData<Project[]>(['projects'], old =>
        old?.map(p => p.id === id ? { ...p, favorite } : p) ?? []
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['projects'], ctx.prev);
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', vars.id] });
    },
  });
}

export interface CreateProjectInput {
  name: string;
  description: string;
  companyFirmName: string;
  promoCode: string;
  organizationId: string | null;
  usageType: string;
  token: string;
}

export function useCreateProject() {
  return useToastMutation(
    ['projects'],
    (input: CreateProjectInput) => api.post<Project>('/project', input),
    {
      success: () => ({ title: 'Project added successfully', description: 'You should receive an email shortly with your project information', timeout: 5000 }),
    },
  );
}
